/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { Percent, RotateCcw, Save, AlertTriangle } from "lucide-react";
import { fmtDT } from "../lib/date";

const CAT_ORDER = ["material", "labor", "subcontractor", "permit", "equipment", "site_facilities", "logistics", "waste_disposal", "project_insurance", "testing", "bonds", "design_eng", "other"];

export default function BudgetDefaults() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const canEdit = ["admin", "finance", "ceo"].includes(user.role);
  const [data, setData] = useState(null);
  const [ratios, setRatios] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/finance/budget-defaults");
      setData(data); setRatios(Object.fromEntries(Object.entries(data.ratios).map(([k, v]) => [k, String(v)])));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const total = useMemo(() => Object.values(ratios).reduce((s, v) => s + (Number(v) || 0), 0), [ratios]);
  const dirty = data && Object.keys(ratios).some((k) => Number(ratios[k]) !== Number(data.ratios[k]));
  const label = (k) => { const l = data?.labels?.[k] || k; return zh ? l.split(" ")[0] : (l.split(" ").slice(1).join(" ") || l); };

  const save = async (payload) => {
    setSaving(true);
    try {
      const body = payload || Object.fromEntries(Object.entries(ratios).map(([k, v]) => [k, Number(v) || 0]));
      if (Object.values(body).some((v) => v < 0 || v > 100)) { toast.error(zh ? "比例需在 0–100 之间" : "Ratio must be 0–100"); setSaving(false); return; }
      await api.put("/finance/budget-defaults", { ratios: body });
      toast.success(zh ? "默认预算比例已保存，新预算立即生效" : "Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };

  if (!data) return <div className="p-6 text-sm text-slate-400" data-testid="budget-defaults-loading">{zh ? "加载中…" : "Loading…"}</div>;
  const margin = 100 - total;
  return (
    <div className="max-w-4xl space-y-5" data-testid="budget-defaults-page">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-brand-500/10 flex items-center justify-center shrink-0"><Percent className="h-6 w-6 text-brand-500" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{zh ? "预算比例设置" : "Default Budget Ratios"}</h1>
          <p className="text-sm text-slate-500 mt-1">{zh ? "项目未按科目手动设预算时，系统按「合同额 × 比例」自动生成该科目预算；实际支出 ≥ 预算 80% 琥珀提醒、超 100% 红色提醒工程经理与项目经理。" : "When a project has no manual budget for a category, budget = contract × ratio. ≥80% amber / >100% red alerts to eng manager & PM."}</p>
          {data.updatedAt && <p className="text-[11px] text-slate-400 mt-1" data-testid="budget-defaults-updated">{zh ? "上次修改" : "Updated"} {fmtDT(data.updatedAt)} · {data.updatedBy || "—"}</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <ResponsiveTable mobile="scroll" className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left font-medium px-5 py-3">{zh ? "成本科目" : "Category"}</th>
            <th className="text-right font-medium px-4 py-3 w-40">{zh ? "占合同额 %" : "% of contract"}</th>
            <th className="text-right font-medium px-4 py-3 w-40">{zh ? "系统默认" : "System default"}</th>
            <th className="text-right font-medium px-5 py-3 w-44">{zh ? "示例：合同 $200,000" : "e.g. $200,000 contract"}</th>
          </tr></thead>
          <tbody>
            {CAT_ORDER.filter((k) => k in data.ratios).map((k) => {
              const v = Number(ratios[k]) || 0;
              const changed = v !== Number(data.defaults[k]);
              return (
                <tr key={k} className="border-t border-slate-100" data-testid={`budget-ratio-row-${k}`}>
                  <td className="px-5 py-2.5 text-slate-800">{label(k)}<span className="ml-2 text-[11px] text-slate-400 font-mono">{k}</span></td>
                  <td className="px-4 py-2.5 text-right">
                    <input data-testid={`budget-ratio-input-${k}`} type="number" min="0" max="100" step="0.5" disabled={!canEdit} value={ratios[k] ?? ""}
                      onChange={(e) => setRatios((s) => ({ ...s, [k]: e.target.value }))}
                      className={`h-8 w-24 text-right rounded-md border px-2 text-sm ${changed ? "border-amber-400 bg-amber-50" : "border-slate-200"} disabled:bg-slate-50 disabled:text-slate-500`} />
                    <span className="ml-1 text-slate-400">%</span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-400">{data.defaults[k]}%</td>
                  <td className="px-5 py-2.5 text-right text-slate-600 font-mono">${Math.round(200000 * v / 100).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td className="px-5 py-3 font-semibold text-slate-800">{zh ? "合计" : "Total"}</td>
              <td className={`px-4 py-3 text-right font-bold ${total > 100 ? "text-red-600" : "text-slate-900"}`} data-testid="budget-ratio-total">{total.toFixed(1)}%</td>
              <td className="px-4 py-3 text-right text-slate-400">{Object.values(data.defaults).reduce((s, v) => s + v, 0).toFixed(1)}%</td>
              <td className="px-5 py-3 text-right text-slate-600 font-mono">${Math.round(200000 * total / 100).toLocaleString()}</td>
            </tr>
            <tr>
              <td colSpan={4} className={`px-5 pb-3 text-xs ${margin < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="budget-ratio-margin">
                {margin < 0 ? <><AlertTriangle className="inline h-3.5 w-3.5 mr-1" />{zh ? `预算合计已超过合同额 ${(-margin).toFixed(1)}%，请调低` : `Budgets exceed contract by ${(-margin).toFixed(1)}%`}</> : (zh ? `预估毛利空间 ${margin.toFixed(1)}%（合同额减去全部科目预算）` : `Estimated gross margin headroom ${margin.toFixed(1)}%`)}
              </td>
            </tr>
          </tfoot>
        </ResponsiveTable>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canEdit ? (
          <>
            <Button data-testid="budget-defaults-save" onClick={() => save()} disabled={saving || !dirty} className="bg-brand-500 hover:bg-brand-600"><Save className="h-4 w-4 mr-1" />{zh ? "保存比例" : "Save"}</Button>
            <Button data-testid="budget-defaults-reset" variant="outline" disabled={saving} onClick={() => { if (window.confirm(zh ? "恢复为系统默认比例？" : "Restore system defaults?")) save({ ...data.defaults }); }}><RotateCcw className="h-4 w-4 mr-1" />{zh ? "恢复系统默认" : "Restore defaults"}</Button>
          </>
        ) : <p className="text-xs text-slate-400" data-testid="budget-defaults-readonly">{zh ? "只读：仅财务 / 管理员可修改默认比例。" : "Read-only: finance/admin can edit."}</p>}
        <span className="text-xs text-slate-400 ml-auto">{zh ? `预警阈值：接近 ${data.nearPct}% · 超支 100%` : `Alerts: near ${data.nearPct}% · over 100%`}</span>
      </div>
    </div>
  );
}
